/**
 * CSV import.
 *
 * Bank and wallet exports never agree on column names, so the header is mapped
 * by synonym with an explicit override available in the UI. Rows that cannot be
 * read are reported line by line; they are never silently dropped and never
 * filled in with a guess.
 */
import { parseAmountToSen } from '../money.js';
import { DEFAULT_TIME_ZONE } from '../time.js';
import { suggestCategory } from '../rules.js';
import { createTransaction } from '../transaction.js';
import {
  TRANSACTION_TYPES,
  type Category,
  type MerchantRule,
  type Transaction,
  type TransactionType,
} from '../schemas.js';
import { datePartsToIso, parseReceiptDate } from '../parsers/common.js';

/** RFC 4180 parser: quoted fields, escaped quotes, embedded newlines, CRLF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let index = 0;

  const input = text.replace(/^\ufeff/, '');

  while (index < input.length) {
    const char = input[index] as string;

    if (inQuotes) {
      if (char === '"') {
        if (input[index + 1] === '"') {
          field += '"';
          index += 2;
          continue;
        }
        inQuotes = false;
        index += 1;
        continue;
      }
      field += char;
      index += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      index += 1;
      continue;
    }
    if (char === ',') {
      row.push(field);
      field = '';
      index += 1;
      continue;
    }
    if (char === '\r') {
      index += 1;
      continue;
    }
    if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      index += 1;
      continue;
    }
    field += char;
    index += 1;
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows.filter((entry) => entry.some((cell) => cell.trim().length > 0));
}

export type CsvField =
  | 'date'
  | 'amount'
  | 'merchant'
  | 'category'
  | 'account'
  | 'type'
  | 'note'
  | 'currency'
  | 'debit'
  | 'credit';

const HEADER_SYNONYMS: Record<CsvField, readonly string[]> = {
  date: ['date', 'transaction date', 'posting date', 'datetime', 'date time', 'date & time', 'tarikh', 'paid at', 'occurred at', 'timestamp'],
  amount: ['amount', 'amount (myr)', 'amount myr', 'value', 'transaction amount', 'total', 'jumlah', 'amount_sen', 'amountsen'],
  merchant: ['merchant', 'description', 'details', 'transaction details', 'payee', 'narrative', 'particulars', 'name', 'reference'],
  category: ['category', 'kategori', 'type of expense', 'expense category'],
  account: ['account', 'card', 'account label', 'payment method', 'paid with', 'wallet', 'source account'],
  type: ['type', 'transaction type', 'direction', 'dr/cr', 'debit/credit'],
  note: ['note', 'notes', 'memo', 'remarks', 'comment'],
  currency: ['currency', 'ccy', 'curr'],
  debit: ['debit', 'withdrawal', 'money out', 'out', 'paid out', 'dr'],
  credit: ['credit', 'deposit', 'money in', 'in', 'paid in', 'cr'],
};

export type CsvMapping = Partial<Record<CsvField, number>>;

const normalizeHeader = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9&/ ()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * A loose match, used only after every exact match has been assigned.
 *
 * The synonym has to appear as a whole word and be long enough to mean
 * something -- without that, "cr" matches "description" and a bank export ends
 * up treating its merchant column as a credit amount.
 */
function looseMatch(cell: string, synonyms: readonly string[]): boolean {
  return synonyms.some((synonym) => {
    if (synonym.length < 4) return false;
    return new RegExp(`(^|[^a-z0-9])${synonym.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^a-z0-9])`).test(
      cell,
    );
  });
}

/**
 * Guess which column is which from the header row.
 *
 * Two passes -- exact names first, loose names second -- and each column can
 * only be claimed once, so a specific header never loses to a vague one.
 */
export function detectMapping(header: readonly string[]): CsvMapping {
  const mapping: CsvMapping = {};
  const normalized = header.map(normalizeHeader);
  const claimed = new Set<number>();
  const fields = Object.entries(HEADER_SYNONYMS) as [CsvField, readonly string[]][];

  for (const [field, synonyms] of fields) {
    const index = normalized.findIndex((cell, position) => !claimed.has(position) && synonyms.includes(cell));
    if (index !== -1) {
      mapping[field] = index;
      claimed.add(index);
    }
  }

  for (const [field, synonyms] of fields) {
    if (mapping[field] !== undefined) continue;
    const index = normalized.findIndex(
      (cell, position) => !claimed.has(position) && cell.length > 2 && looseMatch(cell, synonyms),
    );
    if (index !== -1) {
      mapping[field] = index;
      claimed.add(index);
    }
  }

  // A debit/credit pair is more precise than a single signed amount column.
  if (mapping.debit !== undefined && mapping.credit !== undefined) {
    delete mapping.amount;
  }
  return mapping;
}

/** Column names the header parser understands, for the mapping UI. */
export function knownHeaders(): Record<CsvField, readonly string[]> {
  return HEADER_SYNONYMS;
}

export interface CsvRowError {
  /** 1-based line number in the original file, header included. */
  line: number;
  message: string;
  raw: string;
}

export interface CsvImportOptions {
  mapping?: CsvMapping;
  /** Set when the file has no header row. */
  hasHeader?: boolean;
  accountLabel?: string;
  categories?: readonly Category[];
  rules?: readonly MerchantRule[];
  history?: readonly { merchant: string; categoryId: string; count: number }[];
  defaultCategoryId?: string;
  timeZone?: string;
  currency?: string;
  now?: Date;
  uuid?: () => string;
}

export interface CsvImportResult {
  transactions: Transaction[];
  errors: CsvRowError[];
  mapping: CsvMapping;
  header: string[];
  /** Rows read, excluding the header. */
  rowCount: number;
}

function readType(value: string | undefined): TransactionType | undefined {
  if (!value) return undefined;
  const text = value.trim().toLowerCase();
  if (!text) return undefined;
  if ((TRANSACTION_TYPES as readonly string[]).includes(text)) return text as TransactionType;
  if (['debit', 'dr', 'out', 'withdrawal', 'spend', 'purchase', 'payment'].includes(text)) {
    return 'expense';
  }
  if (['credit', 'cr', 'in', 'deposit', 'salary'].includes(text)) return 'income';
  if (['transfer', 'reload', 'topup', 'top up'].includes(text)) return 'transfer';
  if (['refund', 'reversal', 'chargeback'].includes(text)) return 'refund';
  return undefined;
}

/**
 * Map CSV rows to transactions.
 *
 * Sign convention when no type column is present: a negative amount is a
 * refund (money coming back on an expense line), a positive amount is an
 * expense. A debit/credit column pair overrides this entirely.
 */
export function importCsv(text: string, options: CsvImportOptions = {}): CsvImportResult {
  const rows = parseCsv(text);
  const errors: CsvRowError[] = [];
  const transactions: Transaction[] = [];

  if (rows.length === 0) {
    return { transactions, errors, mapping: {}, header: [], rowCount: 0 };
  }

  const firstRow = rows[0] as string[];
  const hasHeader =
    options.hasHeader ?? firstRow.some((cell) => /[a-z]/i.test(cell) && !/^\s*[\d.,-]+\s*$/.test(cell));
  const header = hasHeader ? firstRow : firstRow.map((_unused, index) => `Column ${index + 1}`);
  const mapping = options.mapping ?? (hasHeader ? detectMapping(header) : {});
  const dataRows = hasHeader ? rows.slice(1) : rows;

  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE;
  const now = options.now ?? new Date();

  if (mapping.amount === undefined && mapping.debit === undefined && mapping.credit === undefined) {
    errors.push({
      line: 1,
      message:
        'No amount column found. Map one of the columns to "amount", or to a debit/credit pair.',
      raw: header.join(','),
    });
    return { transactions, errors, mapping, header, rowCount: dataRows.length };
  }

  const cellAt = (row: readonly string[], index: number | undefined): string | undefined =>
    index === undefined ? undefined : row[index]?.trim();

  dataRows.forEach((row, rowIndex) => {
    const line = rowIndex + (hasHeader ? 2 : 1);
    const raw = row.join(',');

    try {
      let amountSen: number | undefined;
      let type = readType(cellAt(row, mapping.type));

      // An explicit amount column wins; debit/credit is the fallback shape.
      const usePair = mapping.amount === undefined;
      const debitText = usePair ? cellAt(row, mapping.debit) : undefined;
      const creditText = usePair ? cellAt(row, mapping.credit) : undefined;

      if (debitText || creditText) {
        if (debitText) {
          amountSen = Math.abs(parseAmountToSen(debitText));
          type ??= 'expense';
        } else if (creditText) {
          amountSen = Math.abs(parseAmountToSen(creditText));
          type ??= 'income';
        }
      } else {
        const amountText = cellAt(row, mapping.amount);
        if (!amountText) throw new Error('Amount is empty');
        const signed = parseAmountToSen(amountText);
        amountSen = Math.abs(signed);
        if (!type) type = signed < 0 ? 'refund' : 'expense';
      }

      if (amountSen === undefined || amountSen === 0) {
        throw new Error('Amount is zero or missing');
      }

      const dateText = cellAt(row, mapping.date);
      let occurredAt = now.toISOString();
      let dateConfidence = 0.3;
      if (dateText) {
        // A timestamp carrying a zone designator is already absolute -- read it
        // as-is rather than re-interpreting its clock time as local.
        const absolute = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/.test(
          dateText,
        );
        const parsed = absolute ? undefined : parseReceiptDate(dateText, { timeZone, now });
        if (absolute) {
          occurredAt = new Date(dateText).toISOString();
          dateConfidence = 0.99;
        } else if (parsed) {
          occurredAt = datePartsToIso(parsed.parts, timeZone);
          dateConfidence = parsed.confidence;
        } else if (!Number.isNaN(Date.parse(dateText))) {
          occurredAt = new Date(dateText).toISOString();
          dateConfidence = 0.9;
        } else {
          throw new Error(`Could not read the date "${dateText}"`);
        }
      }

      const merchant = cellAt(row, mapping.merchant) ?? '';
      const categoryText = cellAt(row, mapping.category);
      const suggestion = suggestCategory({
        merchant,
        type: type ?? 'expense',
        rules: options.rules ?? [],
        history: options.history ?? [],
        ...(options.defaultCategoryId ? { defaultCategoryId: options.defaultCategoryId } : {}),
      });

      // A category name in the file only counts if it matches a real category.
      const matchedCategory = categoryText
        ? (options.categories ?? []).find(
            (category) =>
              category.id === categoryText.toLowerCase() ||
              category.name.toLowerCase() === categoryText.toLowerCase(),
          )
        : undefined;

      const confidence = Math.min(
        matchedCategory ? 0.95 : suggestion.confidence + 0.1,
        dateConfidence,
        merchant ? 0.95 : 0.5,
      );

      transactions.push(
        createTransaction({
          type: type ?? 'expense',
          amountSen,
          currency: cellAt(row, mapping.currency) ?? options.currency ?? 'MYR',
          merchant,
          merchantOriginal: merchant,
          categoryId: matchedCategory?.id ?? suggestion.categoryId,
          accountLabel: cellAt(row, mapping.account) ?? options.accountLabel ?? 'Imported',
          source: 'csv-import',
          occurredAt,
          note: cellAt(row, mapping.note) ?? null,
          importConfidence: Math.round(confidence * 100) / 100,
          now,
          ...(options.uuid ? { uuid: options.uuid } : {}),
        }),
      );
    } catch (error) {
      errors.push({
        line,
        message: error instanceof Error ? error.message : 'Row could not be read',
        raw,
      });
    }
  });

  return { transactions, errors, mapping, header, rowCount: dataRows.length };
}

const csvCell = (value: string | number | null | undefined): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const CSV_EXPORT_HEADER = [
  'id',
  'date',
  'type',
  'amount',
  'amount_sen',
  'currency',
  'merchant',
  'merchant_original',
  'category',
  'account',
  'source',
  'review_status',
  'import_confidence',
  'note',
] as const;

/** Full-fidelity export: both the human amount and the exact sen value. */
export function exportCsv(
  transactions: readonly Transaction[],
  categories: readonly Category[] = [],
): string {
  const lines = [CSV_EXPORT_HEADER.join(',')];
  for (const transaction of transactions) {
    const categoryName =
      categories.find((category) => category.id === transaction.categoryId)?.name ??
      transaction.categoryId;
    lines.push(
      [
        transaction.id,
        transaction.occurredAt,
        transaction.type,
        (transaction.amountSen / 100).toFixed(2),
        transaction.amountSen,
        transaction.currency,
        transaction.merchantNormalized,
        transaction.merchantOriginal,
        categoryName,
        transaction.accountLabel,
        transaction.source,
        transaction.reviewStatus,
        transaction.importConfidence,
        transaction.note ?? '',
      ]
        .map(csvCell)
        .join(','),
    );
  }
  return `${lines.join('\n')}\n`;
}
